import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase/browser';
import { useAuth } from '../contexts/AuthContextCore';

export function useActivityTracker() {
  const { user } = useAuth();
  const location = useLocation();
  const sessionId = useRef(crypto.randomUUID());
  
  // Track current page state
  const currentPage = useRef(location.pathname);
  const startTime = useRef(Date.now());

  const updatePresence = useCallback(async (status: string = 'online') => {
    if (!user) return;
    try {
      await supabase
        .from('user_presence')
        .upsert({
          user_id: user.id,
          last_active_at: new Date().toISOString(),
          current_page: location.pathname,
          status,
          metadata: {
            page_title: document.title,
            user_agent: navigator.userAgent
          }
        });
    } catch (err) {
      console.error('Error updating presence:', err);
    }
  }, [user, location.pathname]);

  const logActivityStart = useCallback(async (path: string) => {
    if (!user) return;
    try {
      await supabase
        .from('user_activity_logs')
        .insert({
          user_id: user.id,
          page_path: path,
          page_title: document.title,
          session_id: sessionId.current,
          user_agent: navigator.userAgent,
          started_at: new Date().toISOString(),
          action: 'view'
        });
    } catch (err) {
      console.error('Error logging activity start:', err);
    }
  }, [user]);

  const logActivityEnd = useCallback(async (path: string, durationMs: number) => {
    if (!user) return;
    const durSec = Math.floor(durationMs / 1000);
    
    try {
      // Find the open record for this session/path
      const { data } = await supabase
        .from('user_activity_logs')
        .select('id, page_path, started_at')
        .eq('user_id', user.id)
        .eq('session_id', sessionId.current)
        .eq('page_path', path)
        .is('ended_at', null)
        .order('started_at', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        const recordId = data[0].id;
        await supabase
          .from('user_activity_logs')
          .update({
            ended_at: new Date().toISOString(),
            duration_seconds: durSec
          })
          .eq('id', recordId);
      }
    } catch (err) {
      console.error('Error logging activity end:', err);
    }
  }, [user]);

  const updatePresenceRef = useRef(updatePresence);
  const logActivityStartRef = useRef(logActivityStart);
  const logActivityEndRef = useRef(logActivityEnd);

  useEffect(() => {
    updatePresenceRef.current = updatePresence;
    logActivityStartRef.current = logActivityStart;
    logActivityEndRef.current = logActivityEnd;
  }, [logActivityEnd, logActivityStart, updatePresence]);

  // Unified Effect
  useEffect(() => {
    if (!user) return;

    // Start tracking the initial page
    updatePresenceRef.current();
    logActivityStartRef.current(currentPage.current);

    // Heartbeat
    const interval = setInterval(() => updatePresenceRef.current(), 30000);

    return () => {
      // Cleanup on unmount
      const duration = Date.now() - startTime.current;
      logActivityEndRef.current(currentPage.current, duration);
      clearInterval(interval);
      updatePresenceRef.current('away');
    };
  }, [user]); // Only re-run if user changes

  // Path change logic
  useEffect(() => {
    if (location.pathname !== currentPage.current) {
      // 1. End previous page
      const duration = Date.now() - startTime.current;
      logActivityEnd(currentPage.current, duration);

      // 2. Update tracking state
      currentPage.current = location.pathname;
      startTime.current = Date.now();

      // 3. Start new page
      updatePresence();
      logActivityStart(location.pathname);
    }
  }, [location.pathname, logActivityEnd, logActivityStart, updatePresence]);

  const logAction = async (action: string, metadata: any = {}) => {
    if (!user) return;
    try {
      await supabase
        .from('user_activity_logs')
        .insert({
          user_id: user.id,
          page_path: location.pathname,
          page_title: document.title,
          session_id: sessionId.current,
          action,
          metadata,
          started_at: new Date().toISOString(),
          ended_at: new Date().toISOString(),
          duration_seconds: 0
        });
    } catch (err) {
      console.error('Error logging action:', err);
    }
  };

  return { logAction };
}
